/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Apr 22 2025
 **/

import { EventHandler } from "@rfice/eventhandler";

// import { ToastCommandArgs } from "@/frameworks/designStuff/components/Contents/Toast/ToastContainer";

/**
 * @description
 * [XR] Frontend 에러 정의
 * @link : https://docs.google.com/spreadsheets/d/120Wq1GfVYO87KfYH6nWtl_4_3VTFx-9FSh4w30bvawM/edit?pli=1&gid=1966344615#gid=1966344615
 */

export interface CustomErrorOptions<T> extends ErrorOptions {
	cause: T;
	showToast?: boolean;
	errorType?: "error" | "fatal";
}

export interface SystemErrorCause {
	errorCode: string;
	errorMessage: string;
}

/**
 * @description
 * 시스템 오류 클래스
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @created at Tue Apr 22 2025
 */
export class SystemError extends Error {
	public readonly cause: SystemErrorCause;

	constructor(message: string, options: CustomErrorOptions<SystemErrorCause>) {
		super(message, options);
		this.name = "SystemError";
		this.cause = options.cause;
		this._activateErrorProcess(options);
	}

	public static isSystemError(error: SystemError): error is SystemError {
		return error instanceof SystemError;
	}

	public static getErrorCode(error: SystemError): string {
		if (SystemError.isSystemError(error)) {
			return error.cause.errorCode;
		}

		return "unknown";
	}

	public static isValidateErrorCode(error: SystemError): boolean {
		const errorCode = SystemError.getErrorCode(error);

		if (errorCode.match("unknown")) {
			return false;
		} else {
			return true;
		}
	}
	/**
	 * 시스템 에러 카테고리 분류
	 * @param error 시스템 에러 객체
	 * @returns 에러 카테고리 코드
	 *
	 * 카테고리 코드 정의:
	 * - 1: 응용 프로그램
	 * - 2: Core Engine
	 * - 3: 기반기술2팀 개발 모듈
	 * - 4: Web Backend
	 * - 5: Mac 프로그램
	 * - 6: Android 프로그램
	 * - 7: iOS 프로그램
	 * - 8: Web Frontend
	 */
	public static getSystemErrorCategory(error: SystemError): string {
		if (SystemError.isValidateErrorCode(error)) {
			const errorCode = SystemError.getErrorCode(error);
			const category = errorCode[0];
			return category;
		} else {
			return "unknown";
		}
	}

	public static formatClientErrorContent(error: SystemError): string {
		const errorCode = SystemError.getErrorCode(error);
		const errorMessage = error.message;
		return `${errorMessage} (${errorCode})`;
	}

	/**
	 * 시스템 에러에 대한 공통 조치사항
	 */
	private _activateErrorProcess(options: CustomErrorOptions<SystemErrorCause>) {
		if (options.showToast) {
			EventHandler.emit("Command::Toast", ...this._foramtToast(options));
		}
	}

	private _foramtToast(options: CustomErrorOptions<SystemErrorCause>) {
		const errorID = options.cause?.errorCode ?? "unknown";
		const errorType = options.errorType ?? "error";
		const errorMessage = SystemError.formatClientErrorContent(this);
		const errorPermanent = options.errorType === "fatal" ? true : false;
		const errorAutoHide = options.errorType === "fatal" ? false : true;

		return [
			"handleShow",
			{
				id: errorID,
				type: errorType,
				message: errorMessage,
				permanent: errorPermanent,
				autoHide: errorAutoHide
			}
		];
	}
}
